import { Provider } from '@angular/core';
import { RESULTS_MONTHLY_CONFIG, RESULTS_PERIOD_FILTER_CONFIG } from '@celerofinancas/ui-reports';

/**
 * Where the DFM report state lives: in the URL or in the component.
 *
 * `ui-reports` keeps month, year, sector and the selected tab in query params when
 * `useQueryParams` is on — the package default. There are **two** tokens because
 * two components write it: the period filter (`month/year/sector`) and
 * `bfm-monthly` (the DFM tab). Turned off, both publish the same state into the
 * internal `queryParams$`, which is already the source the template reads; no
 * behaviour is lost, it just stops going through the URL.
 *
 * The choice depends on **who owns the address bar**, which is why it lives here
 * and not in the screen's providers: the repo's two bootstraps answer differently,
 * and a provider on the component would apply to both.
 *
 * - **SPA** (`app.config.ts`): the URL is ours. Keeping the period in it makes the
 *   link shareable and survives a reload.
 * - **Custom element** (`element.module.ts`): the URL belongs to the host portal.
 *   Because the bundle registers this application's routes, the package's
 *   navigation resolves against them and rewrites the embedder's address bar —
 *   measured on a local host page embedding the bundle, the URL turned into
 *   `/design-system?month=8&year=2026`, courtesy of the empty route's `redirectTo`.
 *
 * Only what GetNet decides is provided: the package merges what it receives on top of
 * its own default (`{ ...RESULTS_MONTHLY_DEFAULTS_CONFIG, ...provided }`), so repeating
 * the other fields here would only create a copy to grow stale.
 *
 * ## Why two things that are not state ride along
 *
 * `tabsWrapperPosition` and the empty-state copy have nothing to do with where the
 * state lives, and they are here for a mechanical reason: all three belong to the
 * **same token**. Providing `RESULTS_MONTHLY_CONFIG` again on the reports screen would
 * shadow this one whole — Angular does not merge two providers of one token — and the
 * screen would take the period out of the URL without meaning to. That is also why the
 * screen asserts it does not provide this token (`reports.component.spec.ts`).
 *
 * `'top'` is item 1 of the reports document
 * ([86e33fbc9](https://app.clickup.com/t/86e33fbc9)): the DFM tabs sat to the right of
 * the title, on the same line, and the item asks for title, tabs, description, then the
 * period. The package already draws that order — `tabs-wrapper-top` turns its header
 * row into a `column-reverse` with the 24px gap the item states, and the description
 * (`.tab-subtitle`) already renders after that row.
 *
 * The copy is item 3 ([86e33f9ft](https://app.clickup.com/t/86e33f9ft)), and it took a
 * change in the package to become possible: the text used to be written inside the two
 * tab components, with no way out. `ui-reports` 6.4.0 exposes it
 * ([86e395vn1](https://app.clickup.com/t/86e395vn1)), and this is the consumer side of
 * that.
 *
 * @param useQueryParams whether the period should live in the URL query string
 * @returns the providers that configure the report state for one bootstrap
 */
function reportsStateProviders(useQueryParams: boolean): Provider[] {
  return [
    {
      provide: RESULTS_MONTHLY_CONFIG,
      useValue: {
        useQueryParams,
        tabsWrapperPosition: 'top',
        noContentTitle: 'Vamos analisar seus resultados?',
        noContentSubtitle:
          'Basta escolher o mês e ano que quer consultar no menu acima. A gente organiza tudo para você acompanhar a saúde do seu negócio.',
      },
    },
    { provide: RESULTS_PERIOD_FILTER_CONFIG, useValue: { useQueryParams } },
  ];
}

/**
 * Report state in the URL — for the SPA, which owns the address bar.
 */
export const REPORTS_STATE_IN_URL_PROVIDERS: Provider[] = reportsStateProviders(true);

/**
 * Report state in memory only — for the custom elements bundle.
 */
export const REPORTS_STATE_IN_MEMORY_PROVIDERS: Provider[] = reportsStateProviders(false);
